import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

// Получаем URL API из переменной окружения или используем относительный путь
const rawUrl = (process.env.NEXT_PUBLIC_API_URL || '').trim()

// Пути уже содержат /api/v1/..., поэтому:
// - Если переменная не установлена - используем пустой baseURL (относительные пути)
// - Если установлена (например, https://rnpwb.ru/api) - убираем /api из конца, т.к. пути уже содержат /api/v1/...
let API_BASE_URL: string
if (!rawUrl) {
  // Пустой baseURL - пути будут относительными (например, /api/v1/auth/login)
  // Nginx проксирует /api на backend:8000
  API_BASE_URL = ''
} else {
  // Убираем /api из конца, т.к. пути уже содержат /api/v1/...
  API_BASE_URL = rawUrl.replace(/\/api\/?$/, '')
}

// Создаём основной axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 секунд таймаут
})

// Флаг для предотвращения циклических refresh-запросов
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function shouldHardRedirectToLogin(): boolean {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname || ''
  // На auth-страницах не делаем window.location.href,
  // иначе легко получить цикл перезагрузки.
  if (path.startsWith('/auth/')) return false
  return true
}

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token))
  refreshSubscribers = []
}

// Request interceptor: добавляем access_token из localStorage
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('access_token')
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor: обработка 401 и автоматический refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Если 401 и это не повторный запрос
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Не обрабатываем 401 для эндпоинтов авторизации - это нормальные ошибки
      const authEndpoints = [
        '/api/v1/auth/login',
        '/api/v1/auth/register',
        '/api/v1/auth/email/register',
        '/api/v1/auth/email/register/confirm',
        '/api/v1/auth/telegram/code/login',
        '/api/v1/auth/guest-demo',
      ]
      const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint))
      
      if (isAuthEndpoint) {
        // Для эндпоинтов авторизации просто пробрасываем ошибку
        return Promise.reject(error)
      }

      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) {
        // Нет refresh токена — редирект на логин (только для защищенных эндпоинтов)
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          if (shouldHardRedirectToLogin()) {
            window.location.href = '/auth/login'
          }
        }
        return Promise.reject(error)
      }

      if (!isRefreshing) {
        isRefreshing = true

        try {
          // Запрос на обновление токена
          // Используем apiClient для единообразия, но с прямым путём
          const refreshUrl = API_BASE_URL ? `${API_BASE_URL}/api/v1/auth/refresh` : '/api/v1/auth/refresh'
          const { data } = await axios.post(refreshUrl, {
            refresh_token: refreshToken,
          })

          const newAccessToken = data.access_token
          const newRefreshToken = data.refresh_token

          localStorage.setItem('access_token', newAccessToken)
          localStorage.setItem('refresh_token', newRefreshToken)

          isRefreshing = false
          onTokenRefreshed(newAccessToken)

          // Повторяем оригинальный запрос с новым токеном
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          }
          return apiClient(originalRequest)
        } catch (refreshError) {
          isRefreshing = false
          // Refresh не удался — чистим токены и редирект
          if (typeof window !== 'undefined') {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            if (shouldHardRedirectToLogin()) {
              window.location.href = '/auth/login'
            }
          }
          return Promise.reject(refreshError)
        }
      } else {
        // Если refresh уже идёт, подписываемся на обновление токена
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            resolve(apiClient(originalRequest))
          })
        })
      }
    }

    return Promise.reject(error)
  }
)

